import type { ClerkAccountStatus, User } from "@/interfaces/user";
import { formatDateLong } from "./date-format";

export type UserRowActionKey = "resendInvite" | "markEmailVerified" | "changeEmail";

export interface UserRowActionPerms {
  canEditUsers: boolean;
  isGfgStaff: boolean;
  canChangeEmail: boolean;
}

export interface UserRowActionItem {
  key: UserRowActionKey;
  label: string;
  disabled: boolean;
  reason?: string;
}

export type RowAccountState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; value: ClerkAccountStatus };

type RowUser = Pick<User, "clerkUserId" | "archived">;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether the ••• trigger is worth rendering, before any account lookup. */
export function hasPossibleRowActions(user: RowUser, perms: UserRowActionPerms): boolean {
  if (user.archived) return false;
  if (!user.clerkUserId) return perms.canEditUsers;
  return perms.isGfgStaff || perms.canChangeEmail;
}

function changeEmailItem(account: RowAccountState): UserRowActionItem {
  const base = { key: "changeEmail" as const, label: "Change Email" };
  if (account.status === "idle" || account.status === "loading") {
    return { ...base, disabled: true, reason: "Checking account…" };
  }
  if (account.status === "error") {
    return { ...base, disabled: true, reason: "Couldn't load account details" };
  }
  if (account.value.isSso) {
    return { ...base, disabled: true, reason: "Not available for SSO accounts" };
  }
  return { ...base, disabled: false };
}

/** The menu entries for one user row, in display order. */
export function userRowActions(
  user: RowUser,
  perms: UserRowActionPerms,
  account: RowAccountState,
): UserRowActionItem[] {
  if (user.archived) return [];
  if (!user.clerkUserId) {
    return perms.canEditUsers
      ? [{ key: "resendInvite", label: "Resend Invite", disabled: false }]
      : [];
  }
  const items: UserRowActionItem[] = [];
  if (perms.isGfgStaff && account.status === "ready" && account.value.emailVerified === false) {
    items.push({ key: "markEmailVerified", label: "Mark Email as Verified", disabled: false });
  }
  if (perms.canChangeEmail) items.push(changeEmailItem(account));
  return items;
}

export function formatLastSignIn(iso: string | null): string {
  return iso === null ? "Never" : formatDateLong(iso);
}

export function formatRegistered(iso: string | null): string {
  return iso === null ? "—" : formatDateLong(iso);
}

/** Client-side check for the Change Email field; null means the address is acceptable. */
export function validateNewEmail(input: string, currentEmail: string): string | null {
  const email = input.trim();
  if (email === "") return "Enter an email address.";
  if (!EMAIL_PATTERN.test(email)) return "Enter a valid email address.";
  if (email.toLowerCase() === currentEmail.trim().toLowerCase()) {
    return "That's already this user's email.";
  }
  return null;
}
